import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import sv from "./locales/sv/common.json";
import en from "./locales/en/common.json";
import svA from "./locales/sv/slice-a.json";
import svB from "./locales/sv/slice-b.json";
import svC from "./locales/sv/slice-c.json";
import enA from "./locales/en/slice-a.json";
import enB from "./locales/en/slice-b.json";
import enC from "./locales/en/slice-c.json";

// Övriga språk (ar/fa/tr/ku/so/th) laddas dynamiskt först när användaren
// faktiskt väljer dem – de låg tidigare i startbundlen (~80 kB JSON som
// varje besökare parsade på huvudtråden).
const LAZY_LOCALES: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  ar: () => import("./locales/ar/common.json"),
  fa: () => import("./locales/fa/common.json"),
  tr: () => import("./locales/tr/common.json"),
  ku: () => import("./locales/ku/common.json"),
  so: () => import("./locales/so/common.json"),
  th: () => import("./locales/th/common.json"),
};


export const SUPPORTED_LANGS = [
  { code: "sv", name: "Svenska", flag: "🇸🇪", country: "se", dir: "ltr" as const, hasQuestions: true },
  { code: "en", name: "English", flag: "🇬🇧", country: "gb", dir: "ltr" as const, hasQuestions: true },
  { code: "ar", name: "العربية", flag: "🇸🇦", country: "sa", dir: "rtl" as const, hasQuestions: true },
  { code: "fa", name: "فارسی", flag: "🇮🇷", country: "ir", dir: "rtl" as const, hasQuestions: true },
  { code: "tr", name: "Türkçe", flag: "🇹🇷", country: "tr", dir: "ltr" as const, hasQuestions: true },
  { code: "ku", name: "Kurdî", flag: "🟨", country: null, dir: "ltr" as const, hasQuestions: true },
  { code: "so", name: "Soomaali", flag: "🇸🇴", country: "so", dir: "ltr" as const, hasQuestions: true },
  { code: "th", name: "ไทย", flag: "🇹🇭", country: "th", dir: "ltr" as const, hasQuestions: true },
];


export const LANG_CODES = SUPPORTED_LANGS.map((l) => l.code);

export function langMeta(code: string) {
  return SUPPORTED_LANGS.find((l) => l.code === code) ?? SUPPORTED_LANGS[0];
}

if (!i18n.isInitialized) {
  i18n
    .use(initReactI18next)
    .init({
      resources: {
        sv: { common: { ...sv, ...svA, ...svB, ...svC } },
        en: { common: { ...en, ...enA, ...enB, ...enC } },
        // ar/fa/tr/ku/so/th läggs till via ensureLanguage() vid språkval.

      },
      lng: "sv", // SSR + first paint always sv → no hydration mismatch
      fallbackLng: "sv",
      supportedLngs: LANG_CODES,
      defaultNS: "common",
      ns: ["common"],
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
    });
}

// Laddar in ett lazy-språk och registrerar det innan språket aktiveras.
export async function ensureLanguage(code: string) {
  const loader = LAZY_LOCALES[code];
  if (!loader) return;
  if (i18n.hasResourceBundle(code, "common")) return;
  try {
    const mod = await loader();
    i18n.addResourceBundle(code, "common", mod.default, true, true);
  } catch {}
}

// Byter språk och säkerställer att resurserna finns först.
export async function changeLanguageSafe(code: string) {
  await ensureLanguage(code);
  if (code !== i18n.language) await i18n.changeLanguage(code);
}

// Client-side language detection (runs after hydration only).
//
// Endast ett aktivt val (localStorage) byter språk. Webbläsarens
// navigator.language används INTE: SSR renderar alltid svenska, så ett
// automatiskt byte gav en synlig textväxling på första paint för alla
// besökare med annat webbläsarspråk (~19 % av trafiken). Innehållet är
// svenskt och kanoniskt svenskt – språkväljaren styr resten.
export function detectAndApplyClientLang() {
  if (typeof window === "undefined") return;
  try {
    const stored = window.localStorage.getItem("lang");
    if (!stored) return;
    const chosen = LANG_CODES.includes(stored) ? stored : "sv";
    if (chosen !== i18n.language) void changeLanguageSafe(chosen);
  } catch {}
}


export default i18n;
